import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Activity,
  AlertTriangle,
  CheckCircle,
  Clock,
  XCircle,
  RotateCcw,
  TrendingUp,
  Layers,
  GitMerge,
  CalendarClock,
  RefreshCw,
  Loader2,
} from 'lucide-react';
import { opsApi, type OpsDashboardMetrics } from '@/api/ops.api';

// ── Widget ──────────────────────────────────────────────────────────────────

interface MetricCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ElementType;
  color: string;
  alert?: boolean;
}

const MetricCard = ({ title, value, subtitle, icon: Icon, color, alert }: MetricCardProps) => (
  <div
    className={`bg-white dark:bg-slate-800 rounded-xl border p-5 flex items-start gap-4 ${
      alert ? 'border-red-300 bg-red-50 dark:bg-red-900/20' : ''
    }`}
  >
    <div className={`rounded-lg p-2 ${color}`}>
      <Icon size={20} className="text-white" />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">{title}</p>
      <p className={`text-2xl font-bold mt-0.5 ${alert ? 'text-red-700 dark:text-red-400' : 'text-gray-900 dark:text-gray-100'}`}>
        {value}
      </p>
      {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
    </div>
  </div>
);

// ── Default metrics (shown while loading) ─────────────────────────────────────

const DEFAULT_METRICS: OpsDashboardMetrics = {
  failedJobs:        0,
  pendingApprovals:  0,
  failedPostings:    0,
  unreconciledBanks: 0,
  overdueApprovals:  0,
  integrationBacklog:0,
  recurringDue:      0,
  accrualsDue:       0,
  jobsToday:         0,
  successRateToday:  0,
  lastChecked:       new Date().toISOString(),
};

// ── Page ───────────────────────────────────────────────────────────────────

const REFRESH_INTERVAL_MS = 60_000;

export default function OpsDashboardPage() {
  const [metrics,    setMetrics]    = useState<OpsDashboardMetrics>(DEFAULT_METRICS);
  const [isLoading,  setIsLoading]  = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [isError,    setIsError]    = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchMetrics = useCallback(async () => {
    setIsFetching(true);
    setIsError(false);
    try {
      const data = await opsApi.getDashboard();
      if (data) setMetrics(data);
    } catch {
      setIsError(true);
    } finally {
      setIsLoading(false);
      setIsFetching(false);
    }
  }, []);

  useEffect(() => {
    fetchMetrics();
    timerRef.current = setInterval(fetchMetrics, REFRESH_INTERVAL_MS);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [fetchMetrics]);

  const sections = [
    {
      heading: 'Job Scheduler',
      items: [
        {
          title:    'Failed Jobs',
          value:    metrics.failedJobs,
          subtitle: 'Jobs requiring retry or investigation',
          icon:     XCircle,
          color:    'bg-red-500',
          alert:    metrics.failedJobs > 0,
        },
        {
          title:    'Recurring Journals Due',
          value:    metrics.recurringDue,
          subtitle: 'Templates with next run date today or overdue',
          icon:     CalendarClock,
          color:    'bg-indigo-500',
          alert:    false,
        },
        {
          title:    'Accruals Due',
          value:    metrics.accrualsDue,
          subtitle: 'Accrual recognition runs due this period',
          icon:     RotateCcw,
          color:    'bg-purple-500',
          alert:    false,
        },
      ],
    },
    {
      heading: 'Workflow & Approvals',
      items: [
        {
          title:    'Pending Approvals',
          value:    metrics.pendingApprovals,
          subtitle: 'Documents awaiting approval across all modules',
          icon:     Clock,
          color:    'bg-yellow-500',
          alert:    false,
        },
        {
          title:    'Overdue Approvals',
          value:    metrics.overdueApprovals,
          subtitle: 'Approvals past 48-hour escalation threshold',
          icon:     AlertTriangle,
          color:    'bg-orange-500',
          alert:    metrics.overdueApprovals > 0,
        },
      ],
    },
    {
      heading: 'Posting & Integration',
      items: [
        {
          title:    'Pending Postings',
          value:    metrics.failedPostings,
          subtitle: 'Approved GL journals awaiting posting',
          icon:     TrendingUp,
          color:    'bg-red-400',
          alert:    metrics.failedPostings > 0,
        },
        {
          title:    'Integration Backlog',
          value:    metrics.integrationBacklog,
          subtitle: 'Integration batches queued for processing',
          icon:     Layers,
          color:    'bg-teal-500',
          alert:    metrics.integrationBacklog > 0,
        },
      ],
    },
    {
      heading: 'Banking & Reconciliation',
      items: [
        {
          title:    'Open Reconciliations',
          value:    metrics.unreconciledBanks,
          subtitle: 'Bank reconciliation records in Draft status',
          icon:     GitMerge,
          color:    'bg-cyan-500',
          alert:    false,
        },
      ],
    },
  ];

  const totalAlerts =
    metrics.failedJobs +
    metrics.overdueApprovals +
    metrics.failedPostings +
    metrics.integrationBacklog;

  const lastCheckedTime = new Date(metrics.lastChecked).toLocaleTimeString();

  return (
    <div className="p-6 space-y-6">
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">ERP Operations Dashboard</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Real-time operational health monitoring for myLedger ONE.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {isLoading ? (
            <div className="flex items-center gap-2 px-4 py-2 bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-500 dark:text-gray-400">
              <Loader2 size={16} className="animate-spin" />
              Loading…
            </div>
          ) : isError ? (
            <div className="flex items-center gap-2 px-4 py-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400 font-medium">
              <AlertTriangle size={16} />
              Metrics unavailable
            </div>
          ) : totalAlerts > 0 ? (
            <div className="flex items-center gap-2 px-4 py-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400 font-medium">
              <AlertTriangle size={16} />
              {totalAlerts} alert{totalAlerts > 1 ? 's' : ''} require attention
            </div>
          ) : (
            <div className="flex items-center gap-2 px-4 py-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-sm text-green-700 dark:text-green-400 font-medium">
              <CheckCircle size={16} />
              All systems operational
            </div>
          )}

          <button
            onClick={fetchMetrics}
            disabled={isFetching}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-slate-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/50 disabled:opacity-50"
          >
            <RefreshCw size={14} className={isFetching ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>
      </div>

      {/* ── System Overview ─────────────────────────────────────────────── */}
      <div className="bg-gradient-to-r from-indigo-600 to-indigo-700 rounded-xl p-5 text-white">
        <div className="flex items-center gap-3 mb-3">
          <Activity size={20} />
          <span className="font-semibold">System Overview</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
          <div>
            <p className="text-indigo-200 text-xs">Scheduler Status</p>
            <p className="text-lg font-bold mt-0.5">Active</p>
          </div>
          <div>
            <p className="text-indigo-200 text-xs">Jobs Today</p>
            <p className="text-lg font-bold mt-0.5">
              {isLoading ? '…' : metrics.jobsToday}
            </p>
          </div>
          <div>
            <p className="text-indigo-200 text-xs">Success Rate</p>
            <p className="text-lg font-bold mt-0.5">
              {isLoading ? '…' : `${metrics.successRateToday.toFixed(1)}%`}
            </p>
          </div>
          <div>
            <p className="text-indigo-200 text-xs">Last Check</p>
            <p className="text-lg font-bold mt-0.5">
              {isLoading ? '…' : lastCheckedTime}
            </p>
          </div>
        </div>
      </div>

      {/* ── Metric Sections ─────────────────────────────────────────────── */}
      {sections.map((section) => (
        <div key={section.heading}>
          <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
            {section.heading}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {section.items.map((item) => (
              <MetricCard key={item.title} {...item} />
            ))}
          </div>
        </div>
      ))}

      {/* ── Footer ──────────────────────────────────────────────────────── */}
      <p className="text-xs text-gray-400 text-center">
        Metrics refresh automatically every 60 seconds. Last updated: {isLoading ? '…' : lastCheckedTime}
      </p>
    </div>
  );
}
